import { useQuery } from '@tanstack/react-query';
import { PulseLoader } from 'react-spinners';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { apiGet } from '../../services/_common';
import IssueGuide from '../IssueGuide';
import Slider from 'react-slick';
import { ERROR } from '../../_constants';
import { useTranslation } from 'react-i18next';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const imageBoardSettings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 1000,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false, // autoplay 직접 제어
  autoplaySpeed: 10000, // 10초
  fade: true,
  cssEase: 'linear',
};

const overlayText = [
  { imgSrc: '/img/dashboard_img1.png', title: '2025.04.23', description: '건설 현장 CCTV' },
  { imgSrc: '/img/dashboard_img2.png', title: '2025.01.17', description: '통합 CCTV 모니터링 시스템' },
  { imgSrc: '/img/dashboard_img3.png', title: '2024.03.21', description: '슬라이딩 게이트' },
  { imgSrc: '/img/dashboard_img5.png', title: '2024.08.23', description: '통합 환경센서' },
];

// 사내용 이미지 게시판 (임시)
export const ImageBoardTempFading = ({ refetchInterval, uniqueKey }: { refetchInterval: number; uniqueKey: string }) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showOverlay, setShowOverlay] = useState(true);
  const sliderRef = useRef<Slider>(null);

  // const {
  //   data: siteImageBoardData,
  //   isLoading,
  //   isError,
  // } = useQuery(['siteImageBoardGet', uniqueKey], () => fetchData(), {
  //   ...(refetchInterval !== undefined && { refetchInterval }), // 30분
  //   refetchOnWindowFocus: true,
  // });

  // 슬라이드 전환 직후 overlay 표시
  const handleSlideChange = (index: number) => {
    setCurrentSlide(index);
    setShowOverlay(true);
  };

  // const fetchData = async () => {
  //   try {
  //     // 사내용 이미지 게시판 임시 (H001, S0001)
  //     const res = await apiGet({ path: '/safe/imageboardcontents', req: { hCd: 'H001', sCd: 'S0001' } });
  //     if (res.status === 200) return res.data.data.imageBoardTList;
  //     return undefined;
  //   } catch (error) {
  //     console.error(error);
  //     throw new Error('error');
  //   }
  // };

  // const loading = isLoading || !siteImageBoardData;

  // if (isError) return <IssueGuide type='error' text={ERROR} />;
  // if (loading) {
  //   return (
  //     <div className='centered-content'>
  //       <PulseLoader color='rgb(0, 122, 255)' size='10px' />
  //     </div>
  //   );
  // }

  return (
    <ImageBoardContainer>
      <SliderContainer>
        <StyledSlider {...imageBoardSettings} ref={sliderRef} afterChange={handleSlideChange}>
          {overlayText.map((el: any, i: number) => (
            <SlideItem key={`${i}`}>
              <SlideImage src={el.imgSrc} alt={`현장 사진 ${i + 1}`} />
              {showOverlay && currentSlide === i && (
                <ProgressBarWrapper>
                  <ProgressBar
                    duration={imageBoardSettings.autoplaySpeed}
                    trigger={currentSlide}
                    key={currentSlide}
                    onComplete={() => {
                      setShowOverlay(false);
                      if (sliderRef.current) {
                        sliderRef.current.slickNext();
                      }
                    }}
                  />
                </ProgressBarWrapper>
              )}
              {showOverlay && currentSlide === i && (
                <OverlayText>
                  {/* <OverlayTitle>{overlayText[i].title}</OverlayTitle> */}
                  <OverlayDescription>{overlayText[i].description}</OverlayDescription>
                </OverlayText>
              )}
            </SlideItem>
          ))}
        </StyledSlider>
      </SliderContainer>
    </ImageBoardContainer>
  );
};

// ProgressBar 컴포넌트
const ProgressBar: React.FC<{ duration: number; trigger: any; onComplete: () => void }> = ({ duration, trigger, onComplete }) => {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (barRef.current) {
      barRef.current.style.transition = 'none';
      barRef.current.style.width = '0%';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (barRef.current) {
            barRef.current.style.transition = `width ${duration}ms linear`;
            barRef.current.style.width = '100%';
          }
        });
      });
      // duration 후 onComplete 호출
      const timer = setTimeout(() => {
        onComplete();
      }, duration);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [trigger, duration, onComplete]);

  return (
    <div
      ref={barRef}
      style={{
        height: '100%',
        background: 'linear-gradient(90deg, #3e97ff 0%, #6dd5ed 100%)',
        width: '0%',
      }}
    />
  );
};

// Styled Components
const ImageBoardContainer = styled.div`
  &.imageBoard.tempImageBoard {
    .widget-header {
      margin-bottom: 15px;
    }
  }
`;

const SliderContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSlider = styled(Slider)`
  .slick-slide {
    position: relative;
  }
`;

const SlideItem = styled.div`
  position: relative;
  width: 100%;
  height: auto; // 고정 높이 제거
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SlideImage = styled.img`
  width: 100%;
  height: auto;
  /* max-height: 400px; // 필요시 최대 높이만 제한 */
  object-fit: cover; // 잘림 없이 전체 이미지 표시
  display: block;
`;

const OverlayText = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 30%;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.54) 34.15%, rgba(0, 0, 0, 0) 66.88%);
  color: white;
  padding: 1.875rem 1.25rem 1.25rem;
  opacity: 0;
  animation: fadeInUp 1s ease-out forwards;
  z-index: 2;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const OverlayTitle = styled.h3`
  margin: 0 0 5px 0;
  font-size: 24px;
  font-weight: 200;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;

const OverlayDescription = styled.p`
  margin: 0;
  font-size: 2.5rem;
  font-weight: bold;
  line-height: 1.2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  position: absolute;
  bottom: 2rem;
  left: 1.25rem;
`;

// ProgressBarWrapper를 SlideItem의 하단에 항상 고정
const ProgressBarWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 6px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 10;
`;
